import { useCallback, useMemo, useState } from "react";

import { Dropdown } from "@/components";
import { Forward, Settings, StreamQuality } from "@/icons";
import { useTranslate } from "@/providers/context";

import { VideoQualityOptions } from "../HLSVideoPlayer";

const SETTINGS_KEY = "Settings";
const SPEED_KEY = "Playback Speed";
const QUALITY_KEY = "Video Quality";

interface SettingsDropdownProps {
  qualities: VideoQualityOptions[];
  currentQuality: number;
  currentSpeed: number;
  onQualityChange: (value: number) => void;
  onSpeedChange: (value: number) => void;
}

export const SettingsDropdown = ({
  qualities,
  currentQuality,
  currentSpeed,
  onQualityChange,
  onSpeedChange,
}: SettingsDropdownProps) => {
  const { translate } = useTranslate();

  const [open, setOpen] = useState(false);
  const [isAutoQuality, setIsAutoQuality] = useState(true);
  const [visibleMenu, setVisibleMenu] = useState(SETTINGS_KEY);

  const changeMenu = (key: string) => {
    setVisibleMenu(key);
    setTimeout(() => {
      setOpen(true);
    }, 100);
  };

  const mainOptions = useMemo(
    () => [
      {
        text: translate(SPEED_KEY),
        icon: <Forward className="h-4 w-4" />,
        onClick: () => changeMenu(SPEED_KEY),
      },
      {
        text: translate(QUALITY_KEY),
        icon: <StreamQuality className="h-4 w-4" />,
        onClick: () => changeMenu(QUALITY_KEY),
      },
    ],
    [translate],
  );

  const speedOptions = useMemo(
    () =>
      [
        { text: "0.25x", value: 0.25 },
        { text: "0.5x", value: 0.5 },
        { text: "0.75x", value: 0.75 },
        { text: translate("Normal"), value: 1 },
        { text: "1.25x", value: 1.25 },
        { text: "1.5x", value: 1.5 },
        { text: "1.75x", value: 1.75 },
        { text: "2x", value: 2 },
      ].map(({ text, value }) => ({
        text,
        onClick: () => {
          onSpeedChange(value);
          setVisibleMenu(SETTINGS_KEY);
        },
        selected: currentSpeed === value,
      })),
    [translate, currentSpeed, onSpeedChange],
  );

  const handleQualityClick = useCallback(
    (value: number) => {
      onQualityChange(value);
      setVisibleMenu(SETTINGS_KEY);
      setIsAutoQuality(value === -1);
    },
    [onQualityChange, setVisibleMenu, setIsAutoQuality],
  );

  const qualityOptions = useMemo(() => {
    const availableQualities = qualities.map(({ name, value }) => ({
      text: name,
      onClick: () => handleQualityClick(value),
      selected: !isAutoQuality && currentQuality === value,
    }));
    return [
      ...availableQualities.reverse(),
      {
        text: translate("Auto"),
        onClick: () => handleQualityClick(-1),
        selected: isAutoQuality,
      },
    ];
  }, [qualities, currentQuality, isAutoQuality, handleQualityClick, translate]);

  const getVisibleOptions = (key: string) => {
    switch (key) {
      case SPEED_KEY:
        return speedOptions;
      case QUALITY_KEY:
        return qualityOptions;
      default:
        return mainOptions;
    }
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      contentProps={{
        align: "end",
        side: "top",
        sideOffset: 10,
        alignOffset: -3,
      }}
      options={getVisibleOptions(visibleMenu)}
      headerOption={
        visibleMenu === SETTINGS_KEY
          ? undefined
          : {
              text: translate(visibleMenu),
              onClick: () => changeMenu(SETTINGS_KEY),
            }
      }
    >
      <Settings className="h-6 w-6 hover:opacity-85" />
    </Dropdown>
  );
};
